<template>
    <div class="space-y-4">
        <!-- Configurações Gerais -->
        <div class="overflow-hidden rounded-lg border border-gray-200">
            <button
                type="button"
                class="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
                @click="toggle('flags')"
            >
                <div class="flex items-center space-x-2">
                    <span
                        class="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100"
                    >
                        <font-awesome-icon
                            icon="fa-solid fa-cog"
                            class="text-xs text-slate-600"
                        />
                    </span>
                    <span class="text-sm font-semibold text-gray-700"
                        >Configurações Gerais</span
                    >
                </div>
                <font-awesome-icon
                    icon="fa-solid fa-chevron-down"
                    class="text-xs text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': expanded.flags }"
                />
            </button>
            <div
                v-show="expanded.flags"
                class="border-t border-gray-200 px-4 py-4 dark:border-gray-700"
            >
                <div
                    class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4"
                >
                    <div class="flex flex-col gap-1">
                        <span
                            class="text-xs font-medium text-gray-500 dark:text-gray-400"
                            >Recebimento</span
                        >
                        <payment-settings
                            :status="settings.allow_payment_in_installments"
                        />
                    </div>
                    <div class="flex flex-col gap-1">
                        <span
                            class="text-xs font-medium text-gray-500 dark:text-gray-400"
                            >Recebimento como fornecedor</span
                        >
                        <payment-settings
                            :status="
                                settings.allow_payment_in_installments_for_provider
                            "
                        />
                    </div>
                    <div class="flex flex-col gap-1">
                        <span
                            class="text-xs font-medium text-gray-500 dark:text-gray-400"
                            >Reembolso comissão fornecedor</span
                        >
                        <refund-provider-from-producer
                            :status="
                                settings.refund_providers_commission_from_producer
                            "
                        />
                    </div>
                    <div class="flex flex-col gap-1">
                        <span
                            class="text-xs font-medium text-gray-500 dark:text-gray-400"
                            >Retentativa sem 3DS</span
                        >
                        <is-allowed :status="settings.allow_3ds_fallback" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <span
                            class="text-xs font-medium text-gray-500 dark:text-gray-400"
                            >Cobrar taxa no reembolso</span
                        >
                        <is-allowed
                            :status="
                                settings.allow_charge_platform_fee_upon_refund
                            "
                        />
                    </div>
                    <div class="flex flex-col gap-1">
                        <span
                            class="text-xs font-medium text-gray-500 dark:text-gray-400"
                            >Cancelamento de reembolso</span
                        >
                        <is-allowed :status="settings.is_refund_locked" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <span
                            class="text-xs font-medium text-gray-500 dark:text-gray-400"
                            >Saque</span
                        >
                        <is-allowed :status="!settings.is_cashout_locked" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <span
                            class="text-xs font-medium text-gray-500 dark:text-gray-400"
                            >Saque saldo em análise</span
                        >
                        <is-allowed
                            :status="settings.allow_cashout_under_review"
                        />
                    </div>
                    <div class="flex flex-col gap-1">
                        <span
                            class="text-xs font-medium text-gray-500 dark:text-gray-400"
                            >Reserva de saldo</span
                        >
                        <is-allowed
                            :status="settings.balance_reserve_enabled"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Taxas por Método de Pagamento -->
        <div class="overflow-hidden rounded-lg border border-gray-200">
            <button
                type="button"
                class="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
                @click="toggle('fees')"
            >
                <div class="flex items-center space-x-2">
                    <span
                        class="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100"
                    >
                        <font-awesome-icon
                            icon="fa-solid fa-percent"
                            class="text-xs text-blue-600"
                        />
                    </span>
                    <span class="text-sm font-semibold text-gray-700"
                        >Taxas por Método de Pagamento</span
                    >
                </div>
                <div class="flex items-center gap-3">
                    <!-- Compact summary when collapsed -->
                    <div
                        v-if="!expanded.fees"
                        class="hidden items-center gap-2 text-xs text-gray-400 sm:flex"
                    >
                        <span
                            >Fixa
                            {{
                                $root.formatCurrency(
                                    settings.platform_fixed_fee,
                                )
                            }}</span
                        >
                        <span>&middot;</span>
                        <span
                            >Cartão
                            {{ settings.platform_fee_credit_card }}%</span
                        >
                        <span>&middot;</span>
                        <span>Pix {{ settings.platform_fee_pix }}%</span>
                    </div>
                    <font-awesome-icon
                        icon="fa-solid fa-chevron-down"
                        class="text-xs text-gray-400 transition-transform duration-200"
                        :class="{ 'rotate-180': expanded.fees }"
                    />
                </div>
            </button>
            <div
                v-show="expanded.fees"
                class="divide-y divide-gray-100 border-t border-gray-200 dark:divide-gray-700 dark:border-gray-700"
            >
                <!-- Cobrança Fixa -->
                <div class="px-4 py-3">
                    <div
                        class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400"
                    >
                        <font-awesome-icon
                            icon="fa-solid fa-dollar-sign"
                            class="text-emerald-500"
                        />
                        Cobrança Fixa
                    </div>
                    <div class="grid grid-cols-2 gap-4 sm:grid-cols-3">
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Nacional</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{
                                    $root.formatCurrency(
                                        settings.platform_fixed_fee,
                                    )
                                }}
                            </div>
                        </div>
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Internacional</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{
                                    $root.formatCurrency(
                                        settings.international_fixed_fee,
                                    )
                                }}
                            </div>
                        </div>
                        <div v-if="settings.charge_fixed_fee_per_item">
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Combo</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{
                                    $root.formatCurrency(
                                        settings.combo_fixed_fee,
                                    )
                                }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Cartão de Crédito -->
                <div class="px-4 py-3">
                    <div
                        class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400"
                    >
                        <font-awesome-icon
                            icon="fa-solid fa-credit-card"
                            class="text-blue-500"
                        />
                        Cartão de Crédito
                    </div>
                    <div class="grid grid-cols-3 gap-4">
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >MDR</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.platform_fee_credit_card }}%
                            </div>
                        </div>
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Juros por parcela</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.installment_fee_credit_card }}%
                            </div>
                        </div>
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Tempo para saque</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.credit_card_cashout_days }} dias
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Boleto -->
                <div class="px-4 py-3">
                    <div
                        class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400"
                    >
                        <font-awesome-icon
                            icon="fa-solid fa-barcode"
                            class="text-amber-500"
                        />
                        Boleto
                    </div>
                    <div class="grid grid-cols-3 gap-4">
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >MDR</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.platform_fee_bank_slip }}%
                            </div>
                        </div>
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Juros por parcela</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.installment_fee_bank_slip }}%
                            </div>
                        </div>
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Tempo para saque</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.bank_slip_cashout_days }} dias
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pix -->
                <div class="px-4 py-3">
                    <div
                        class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400"
                    >
                        <font-awesome-icon
                            icon="fa-brands fa-pix"
                            class="text-teal-500"
                        />
                        Pix
                    </div>
                    <div class="grid grid-cols-3 gap-4">
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >MDR</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.platform_fee_pix }}%
                            </div>
                        </div>
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Tempo para saque</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.pix_cashout_days }} dias
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pix Recorrente -->
                <div class="px-4 py-3">
                    <div
                        class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400"
                    >
                        <font-awesome-icon
                            icon="fa-brands fa-pix"
                            class="text-teal-500"
                        />
                        Pix Recorrente
                    </div>
                    <div class="grid grid-cols-3 gap-4">
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >MDR</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.platform_fee_pix_recurring }}%
                            </div>
                        </div>
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Tempo para saque</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.pix_recurring_cashout_days }} dias
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Internacional -->
                <div class="px-4 py-3">
                    <div
                        class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400"
                    >
                        <font-awesome-icon
                            icon="fa-solid fa-globe"
                            class="text-purple-500"
                        />
                        Pagamento Internacional
                    </div>
                    <div class="grid grid-cols-3 gap-4">
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >MDR</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.international_platform_fee }}%
                            </div>
                        </div>
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Tempo para saque</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.international_cashout_days }} dias
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Antecipação -->
        <div class="overflow-hidden rounded-lg border border-gray-200">
            <button
                type="button"
                class="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
                @click="toggle('anticipation')"
            >
                <div class="flex items-center space-x-2">
                    <span
                        class="flex h-7 w-7 items-center justify-center rounded-lg bg-yellow-100"
                    >
                        <font-awesome-icon
                            icon="fa-solid fa-coins"
                            class="text-xs text-yellow-600"
                        />
                    </span>
                    <span class="text-sm font-semibold text-gray-700"
                        >Antecipação</span
                    >
                </div>
                <div class="flex items-center gap-3">
                    <div
                        v-if="!expanded.anticipation"
                        class="hidden items-center gap-2 text-xs text-gray-400 sm:flex"
                    >
                        <span
                            :class="
                                settings.can_anticipate
                                    ? 'text-green-500'
                                    : 'text-red-400'
                            "
                        >
                            {{ settings.can_anticipate ? "Ativa" : "Inativa" }}
                        </span>
                        <template v-if="settings.can_anticipate">
                            <span>&middot;</span>
                            <span>{{ settings.anticipation_fee }}%</span>
                            <span>&middot;</span>
                            <span
                                >Limite
                                {{ settings.anticipation_limit ?? 60 }}%</span
                            >
                        </template>
                    </div>
                    <font-awesome-icon
                        icon="fa-solid fa-chevron-down"
                        class="text-xs text-gray-400 transition-transform duration-200"
                        :class="{ 'rotate-180': expanded.anticipation }"
                    />
                </div>
            </button>
            <div
                v-show="expanded.anticipation"
                class="border-t border-gray-200 px-4 py-4 dark:border-gray-700"
            >
                <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div>
                        <span class="text-xs text-gray-500 dark:text-gray-400"
                            >Status</span
                        >
                        <div
                            class="text-sm font-semibold"
                            :class="
                                settings.can_anticipate
                                    ? 'text-green-600'
                                    : 'text-red-500'
                            "
                        >
                            {{ settings.can_anticipate ? "Ativa" : "Inativa" }}
                        </div>
                    </div>
                    <div>
                        <span class="text-xs text-gray-500 dark:text-gray-400"
                            >Versão</span
                        >
                        <div
                            class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                        >
                            {{ settings.anticipation_version }}
                        </div>
                    </div>
                    <div>
                        <span class="text-xs text-gray-500 dark:text-gray-400"
                            >Taxa</span
                        >
                        <div
                            class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                        >
                            {{ settings.anticipation_fee }}%
                        </div>
                    </div>
                    <div>
                        <span class="text-xs text-gray-500 dark:text-gray-400"
                            >Limite</span
                        >
                        <div
                            class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                        >
                            {{ settings.anticipation_limit ?? 60 }}%
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Reserva de Saldo (conditional) -->
        <div
            v-if="settings.balance_reserve_enabled"
            class="overflow-hidden rounded-lg border border-gray-200"
        >
            <button
                type="button"
                class="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
                @click="toggle('reserve')"
            >
                <div class="flex items-center space-x-2">
                    <span
                        class="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-100"
                    >
                        <font-awesome-icon
                            icon="fa-solid fa-shield-halved"
                            class="text-xs text-orange-600"
                        />
                    </span>
                    <span class="text-sm font-semibold text-gray-700"
                        >Reserva de Saldo</span
                    >
                </div>
                <div class="flex items-center gap-3">
                    <div
                        v-if="!expanded.reserve"
                        class="hidden items-center gap-2 text-xs text-gray-400 sm:flex"
                    >
                        <span
                            >{{
                                settings.balance_reserve_percentage || 0
                            }}%</span
                        >
                        <span>&middot;</span>
                        <span
                            >{{
                                settings.balance_reserve_cushion_days || 0
                            }}
                            dias</span
                        >
                    </div>
                    <font-awesome-icon
                        icon="fa-solid fa-chevron-down"
                        class="text-xs text-gray-400 transition-transform duration-200"
                        :class="{ 'rotate-180': expanded.reserve }"
                    />
                </div>
            </button>
            <div
                v-show="expanded.reserve"
                class="border-t border-gray-200 px-4 py-4 dark:border-gray-700"
            >
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <span class="text-xs text-gray-500 dark:text-gray-400"
                            >Prazo de colchão</span
                        >
                        <div
                            class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                        >
                            {{
                                settings.balance_reserve_cushion_days || 0
                            }}
                            dias
                        </div>
                    </div>
                    <div>
                        <span class="text-xs text-gray-500 dark:text-gray-400"
                            >Percentual de reserva</span
                        >
                        <div
                            class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                        >
                            {{ settings.balance_reserve_percentage || 0 }}%
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Cashback e Indicações -->
        <div class="overflow-hidden rounded-lg border border-gray-200">
            <button
                type="button"
                class="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
                @click="toggle('extras')"
            >
                <div class="flex items-center space-x-2">
                    <span
                        class="flex h-7 w-7 items-center justify-center rounded-lg bg-pink-100"
                    >
                        <font-awesome-icon
                            icon="fa-solid fa-gift"
                            class="text-xs text-pink-600"
                        />
                    </span>
                    <span class="text-sm font-semibold text-gray-700"
                        >Cashback e Indicações</span
                    >
                </div>
                <font-awesome-icon
                    icon="fa-solid fa-chevron-down"
                    class="text-xs text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': expanded.extras }"
                />
            </button>
            <div
                v-show="expanded.extras"
                class="divide-y divide-gray-100 border-t border-gray-200 dark:divide-gray-700 dark:border-gray-700"
            >
                <!-- Cashback -->
                <div class="px-4 py-3">
                    <div
                        class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400"
                    >
                        <font-awesome-icon
                            icon="fa fa-money-bill-transfer"
                            class="text-green-500"
                        />
                        Cashback
                    </div>
                    <div class="grid grid-cols-3 gap-4">
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Tipo de taxa</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{
                                    settings.cashback_type == "percentage"
                                        ? "Percentual"
                                        : "Valor Fixo"
                                }}
                            </div>
                        </div>
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Valor</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.cashback_fee }}
                            </div>
                        </div>
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Origem</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{
                                    settings.cashback_origin === "platform"
                                        ? "Plataforma"
                                        : settings.cashback_origin ===
                                            "interest"
                                          ? "Juros"
                                          : "Todos"
                                }}
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Indicações -->
                <div class="px-4 py-3">
                    <div
                        class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400"
                    >
                        <font-awesome-icon
                            icon="fa-solid fa-star"
                            class="text-yellow-500"
                        />
                        Programa de Indicações
                    </div>
                    <div class="grid grid-cols-3 gap-4">
                        <div>
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400"
                                >Tempo para saque</span
                            >
                            <div
                                class="text-sm font-semibold text-gray-800 dark:text-gray-100"
                            >
                                {{ settings.referral_cashout_days }} dias
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import IsAllowed from "./IsAllowed.vue";
import PaymentSettings from "./PaymentSettings.vue";
import RefundProviderFromProducer from "./RefundProviderFromProducer.vue";

export default {
    props: ["settings"],
    components: {
        IsAllowed,
        PaymentSettings,
        RefundProviderFromProducer,
    },
    data() {
        return {
            expanded: {
                flags: false,
                fees: true,
                anticipation: false,
                reserve: false,
                extras: false,
            },
        };
    },
    methods: {
        toggle(section) {
            this.expanded[section] = !this.expanded[section];
        },
    },
};
</script>
